import axios, { AxiosResponse } from 'axios';
import { AppError } from '@/errors/appError';
import { addFavoriteEvent, clearFavoriteEvents, removeFavoriteEvent, searchEvent } from '@/api/event';
import { useEventStore } from '@/stores/event';
import { ILiveFeedDataAPI, IPregameFeedDataAPI, ISearchOptions } from '@/types';
import { IEventSearchApiItem, IEventSearchApiListResponse } from '@/types/event';
import { EventPeriod, Line } from '@/constants';
import { useSearchStore } from '@/stores/search';

export const eventService = {
    async addFavorite(customEventId: string): Promise<AxiosResponse | void> {
        const eventStore = useEventStore();

        if (eventStore.favoriteEventProcessing[customEventId]) {
            return;
        }

        eventStore.favoriteEventProcessing[customEventId] = true;
        try {
            const response = await addFavoriteEvent(customEventId);
            eventStore.favoriteEvent[customEventId] = 1;

            return response;
        } catch (e) {
            throw new AppError(`Unable to add favorite event #${customEventId}, request error`, { cause: e });
        } finally {
            delete eventStore.favoriteEventProcessing[customEventId];
        }
    },

    async removeFavorite(customEventId: string): Promise<AxiosResponse | void> {
        const eventStore = useEventStore();

        if (eventStore.favoriteEventProcessing[customEventId]) {
            return;
        }

        eventStore.favoriteEventProcessing[customEventId] = true;
        try {
            const response = await removeFavoriteEvent(customEventId);
            delete eventStore.favoriteEvent[customEventId];

            return response;
        } catch (e) {
            throw new AppError(`Unable to delete favorite event #${customEventId}, request error`, { cause: e });
        } finally {
            delete eventStore.favoriteEventProcessing[customEventId];
        }
    },

    async clearFavorites(): Promise<AxiosResponse | void> {
        try {
            const response = await clearFavoriteEvents();
            const eventStore = useEventStore();
            eventStore.favoriteEvent = {};

            return response;
        } catch (e) {
            throw new AppError(`Unable to clear favorite events, request error`, { cause: e });
        }
    },

    async search(searchQuery: string, options: ISearchOptions): Promise<AxiosResponse<IEventSearchApiListResponse> | void> {
        try {
            const response = await searchEvent(searchQuery, options);

            const searchStore = useSearchStore();
            searchStore.items = await Promise.all(response.data.items.map((item) => this.createFeedItemFromSearchApiItem(item)));
            console.log(searchStore.items);

            return response;
        } catch (e) {
            if (axios.isCancel(e)) {
                console.log('Previous request canceled, new request is sent:', e.message);
            } else {
                throw new AppError(`Unable to search events, request error`, { cause: e });
            }
        }
    },

    async createFeedItemFromSearchApiItem(searchItem: IEventSearchApiItem) {
        let feedRawData;
        if (searchItem.isLive) {
            feedRawData = {} as ILiveFeedDataAPI;
        } else {
            feedRawData = {} as IPregameFeedDataAPI;
        }
        feedRawData.line = Line.Main;
        feedRawData.ceId = searchItem.ceId;
        feedRawData.clId = searchItem.clId;
        feedRawData.clName = searchItem.clName;
        feedRawData.iso3 = searchItem.iso3 ?? undefined;
        feedRawData.ctaId = searchItem.ctaId;
        feedRawData.ctaName = searchItem.ctaName;
        feedRawData.ctaLogo = searchItem.ctaLogo ?? undefined;
        feedRawData.cthId = searchItem.cthId;
        feedRawData.cthName = searchItem.cthName;
        feedRawData.cthLogo = searchItem.cthLogo ?? undefined;
        feedRawData.dg = searchItem.dg;
        if (searchItem.isLive) {
            (feedRawData as ILiveFeedDataAPI).time = searchItem.time ?? undefined;
            (feedRawData as ILiveFeedDataAPI).sc = searchItem.sc ?? undefined;
            (feedRawData as ILiveFeedDataAPI).rAway = searchItem.rAway ?? undefined;
            (feedRawData as ILiveFeedDataAPI).rHome = searchItem.rHome ?? undefined;
        } else {
            (feedRawData as IPregameFeedDataAPI).leftUntilStart = searchItem.leftUntilStart;
        }

        //todo change here!!!
        //return searchItem.isLive ? new FeedLiveItem(EventPeriod.FullTime, feedRawData) : new FeedPregameItem(EventPeriod.FullTime, feedRawData);

        if (searchItem.isLive) {
            const { FeedLiveItem } = await import('@/models/feedLive');
            return new FeedLiveItem(EventPeriod.FullTime, feedRawData);
        } else {
            const { FeedPregameItem } = await import('@/models/feedPregame');
            return new FeedPregameItem(EventPeriod.FullTime, feedRawData);
        }
    },
};
